/* eslint-disable react/require-default-props */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useState, useEffect } from 'react';
import proptypes from 'prop-types';
import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import { QueryContext, QueryProvider, extend } from '@thd-nucleus/data-sources';
import { ThemeProvider } from '@one-thd/sui-atomic-components';
import { getThemeByName } from '@thd-olt-component-react/stencil-themes';
import { usePsSearchModel } from '../hooks/usePsSearchModel';
import { ProductShelfCarousel } from './ProductShelfCarousel';
import classNames from 'classnames';
import './product-shelf-container.scss';
import { Typography, Link } from '@one-thd/sui-atomic-components';

const { bool, instanceOf, number, shape, string } = proptypes;
/**
 * @group Components
 * Displays Product-Shelf component
 */

const ProductShelfContainer = (props) => {

  const { 
    componentId = '',
    channel = 'desktop',
    host = 'https://www.homedepot.com',
    linkType = '_self',
    minDpdCount = 4,
    opts = {},
    showTooFewProductsNotice = false,
    theme = null,
    storeId: propsStoreId = undefined,
    homepageProductShelf = false
  } = props;
  const { isClientResolved, defaultVariables } = useContext(QueryContext);
  const { isBrandPricingPolicyCompliant = false } = defaultVariables?.current;
  const startIndex = 0;
  const pageSize = 24;
  const {
    skus,
    shopAllLink,
    categoryLabel,
    storeId,
    storeZip,
    totalProducts = 0,
    loading
  } = usePsSearchModel({
    ...props, 
    pageSize,
    storefilter: 'ALL',
    startIndex
  });

  const isMobile = channel === 'mobile';
  const [isClient, setIsClient] = useState(false);
  
  useEffect(() => {
    setIsClient(true);
  }, []);
  
  const showProductShelf = totalProducts > minDpdCount;

  let showArrows = isMobile ? totalProducts > 1 : totalProducts > 5;
  const carouselConfig = {
    breakpoints: {
      sm: {
        slidesPerView: 2,
        slidesPerGroup: 2,
      },
      md: {
        slidesPerView: 4,
        slidesPerGroup: 4,
      },
      lg: {
        slidesPerView: homepageProductShelf ? 6 : 4,
        slidesPerGroup: homepageProductShelf ? 6 : 4,
        spaceBetween : 16,
      },
      xl: {
        slidesPerView: homepageProductShelf ? 6 : 5,
        slidesPerGroup: homepageProductShelf ? 6 : 5,
        spaceBetween : 16,
      },
      '2xl': {
        slidesPerView: homepageProductShelf ? 6 : 5,
        slidesPerGroup: homepageProductShelf ? 6 : 5,
        spaceBetween : 16,
      },
    },
    peek: "sm",
    type: "peek",
    layout: "full-bleed",
    containerId: "product-shelf-carousel"
  };

  const skipFn = ({ skip, attributes, queryName }) => {
    if (queryName !== 'product') {
      return skip;
    }
    const isResolved = isClientResolved({ queryName: 'psSearchModel' });
    if (attributes.product.fulfillment && !isResolved) {
      return true;
    }

    return skip;
  };

  const resolvedProductSearch = isClientResolved({ queryName: 'psSearchModel' });
  const hideProductShelf = !showProductShelf && isClient && !loading;

  return (
    <div className={classNames('sui-bg-primary','sui-text-primary',{'!sui-rounded-[12px]': homepageProductShelf})}>
      <ThemeProvider theme={getThemeByName(theme)} ssr>
        <div
          className={classNames(
            'product-shelf',
            'sui-bg-primary',
            'sui-text-primary',
            'sui-w-full',
            'sui-text-left',
            {
              '!sui-rounded-[12px] sui-pl-4 sui-pr-4': homepageProductShelf,
              'sui-min-h-[523px]': !homepageProductShelf,
              'sui-p-4': !homepageProductShelf && !!theme,
              'product-shelf__hide sui-hidden': hideProductShelf,
            },
          )}
          data-component="ProductShelf"
        >
        {!showProductShelf &&
          (
            <>
              <div className="product-shelf__placeholder-label sui-pb-[5px]">
                <LoadingPlaceholder
                  type="rect"
                  ready={false}
                  showLoadingAnimation
                  color="#E0E0E0"
                  style={{ height: "40px", width: "100%" }}
                >
                  Label Placeholder
                </LoadingPlaceholder>
              </div>
              <div>
                <div className="product-shelf__carousel--wrapper sui-p-0 sui-overflow-y-auto sui-flex-[1] no-arrows-displayed">
                  <ProductShelfCarousel carouselConfig={carouselConfig} showPlaceholder />
                </div>
              </div>
            </>
          )
        }
        {showProductShelf &&
          (
            <>
              <div className={classNames(
                'product-shelf__label',
                'sui-flex-wrap',
                'sui-flex',
                'sui-items-center',
                'sui-justify-between',
                {
                  'sui-mb-4': !homepageProductShelf,
                  'sui-pt-4 sui-pb-2': homepageProductShelf,
                })}
              >
                <Typography variant="h2" height="snug" uppercase weight="display"
                  data-contentful-field-id="title" data-contentful-entry-id={props.componentId}
                >{categoryLabel}</Typography>
                {shopAllLink !== '' && (
                  <Link
                    href={shopAllLink}
                  >
                    Shop All
                  </Link>
                )}
              </div>
              <div className={classNames(
                  'product-shelf__dynamic-aisle',
                  'sui-flex',
                  'sui-bg-transparent',
                  'sui-rounded-[3px]',
                  'sui-bg-no-repeat',
                  'sui-bg-cover',
                  'sui-bg-center',
                  {'sm:sui-pl-[10px]': !homepageProductShelf},
                  'max-[640px]:sui-flex-col')}>
                <div className={classNames(
                  'product-shelf__carousel--wrapper',
                  'sui-p-0',
                  'sui-overflow-y-auto',
                  'sui-flex-[1]',
                  {
                    'sui-pb-4': homepageProductShelf,
                    'max-[640px]:sui-m-[7px]': !homepageProductShelf
                  },
                  {
                    'no-arrows-displayed': !showArrows
                  })}
                >
                  <QueryProvider
                    dataSource="searchNav"
                    cacheKey="dpdSearchNav"
                    defaultVariables={{
                      storeId,
                      zipCode: storeZip,
                      skipKPF: true,
                      skipSubscribeAndSave: true,
                      isBrandPricingPolicyCompliant
                    }}
                    skip={skipFn}
                  >
                    <ProductShelfCarousel
                      channel={channel}
                      skus={skus}
                      storeId={storeId}
                      storeZip={storeZip}
                      linkType={linkType}
                      carouselConfig={carouselConfig}
                      theme={theme}
                      homepageProductShelf={homepageProductShelf}
                    />
                  </QueryProvider>
                </div>
              </div>
            </>
          )
        }
      </div>
      </ThemeProvider>
      {showTooFewProductsNotice && hideProductShelf &&
        (
          <div
            className={classNames('product-shelf sui-w-full sui-text-left sui-text-danger')}
            data-component="ProductShelf"
          >
            <p>Not enough valid products ({totalProducts} &lt; {minDpdCount}) to display Product Shelf module</p>
          </div>
        )
      }
    </div>
  );
};

ProductShelfContainer.displayName = 'ProductShelfContainer';

ProductShelfContainer.dataModel = extend(ProductShelfCarousel);

ProductShelfContainer.propTypes = {
  componentId: string,
  channel: string,
  host: string,
  linkType: string,
  minDpdCount: number,
  opts: shape({
    contentfulTitle: string,
    navParam: string,
    itemIds: instanceOf(Array),
    sortBy: string,
    sortOrder: string
  }),
  showTooFewProductsNotice: bool,
  theme: string,
  storeId: string,
  homepageProductShelf: bool
};

export { ProductShelfContainer };
